import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  TextField
} from '@mui/material';
import api from '../services/api';
import {
  CheckCircle,
  Cancel,
  QuestionAnswer,
  Download
} from '@mui/icons-material';

interface ReportData {
  reportUrl: string;
  propertyAddress: string;
  agencyName: string;
  vendorResponse?: string;
  vendorResponseDate?: string;
  vendorSignatureImageData?: string;
  vendorSignatureImageType?: string;
  token?: string; // Token returned from code-based access
}

const VendorReport: React.FC = () => {
  const { token, code } = useParams<{ token?: string; code?: string }>();
  const [accessCode, setAccessCode] = useState(code || '');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [responseSubmitted, setResponseSubmitted] = useState(false);

  const handleUnlockByCode = useCallback(async () => {
    if (!code || code.length !== 6) return;
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.get(`/agencies/vendor/report/code/${code}`);
      setReportData(data);
      // Store token for response submission if provided
      if (data.token) {
        // We'll use the code for response submission, but store token if available
      }
      if (data.vendorResponse) {
        setResponseSubmitted(true);
      }
    } catch (err: any) {
      console.error('Error fetching report by code:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load market appraisal');
    } finally {
      setLoading(false);
    }
  }, [code]);

  // Auto-fetch report if code is provided in URL
  useEffect(() => {
    if (code && code.length === 6) {
      handleUnlockByCode();
    } else if (!token && !code) {
      setError('Invalid access token or code');
    }
  }, [code, handleUnlockByCode, token]);

  const handleUnlock = async () => {
    if (!token) return;
    if (!accessCode || accessCode.length !== 6) return;
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post(`/agencies/vendor/report/${token}`, { code: accessCode });
      setReportData(data);
      if (data.vendorResponse) {
        setResponseSubmitted(true);
      }
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.message || 'Failed to load consultation report');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (response: "I'd like to proceed" | 'Not interested' | 'Need to discuss') => {
    const codeToUse = code || accessCode;
    if (!codeToUse || codeToUse.length !== 6 || submitting || responseSubmitted) return;

    // Determine which token to use: URL param, report data token, or error
    const tokenToUse = token || reportData?.token;
    if (!tokenToUse) {
      setError('Unable to submit response: missing access token');
      return;
    }

    try {
      setSubmitting(true);
      const requestBody: any = { code: codeToUse, response };
      
      await api.post(`/agencies/vendor/report/${tokenToUse}/response`, requestBody);
      setResponseSubmitted(true);
      if (reportData) {
        setReportData({
          ...reportData,
          vendorResponse: response,
          vendorResponseDate: new Date().toISOString()
        });
      }
    } catch (err: any) {
      console.error('Error submitting response:', err);
      setError(err.response?.data?.error || err.message || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
        <Alert severity="error">{error || 'Report not found'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Market Appraisal
          </Typography>
          {reportData && (
            <Typography variant="h6" color="text.secondary" align="center" gutterBottom>
              {reportData.propertyAddress}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" align="center">
            {reportData ? `From ${reportData.agencyName}` : ''}
          </Typography>
          <Divider sx={{ my: 3 }} />
          
          {/* Access code prompt when needed */}
          {!reportData && (
            <Box sx={{ maxWidth: 420, mx: 'auto', mt: 2, mb: 4 }}>
              <Typography variant="body1" gutterBottom align="center">
                Enter your 6-digit access code to view the report
              </Typography>
              <Box component="form" onSubmit={(e) => { e.preventDefault(); handleUnlock(); }} sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <TextField
                  label="Access Code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ maxLength: 6, pattern: '[0-9]{6}', style: { textAlign: 'center', letterSpacing: '0.5rem', fontWeight: 700 } }}
                  size="small"
                />
                <Button variant="contained" disabled={!accessCode || accessCode.length !== 6} onClick={handleUnlock}>
                  Unlock
                </Button>
              </Box>
            </Box>
          )}

          {/* Report PDF Viewer */}
          {reportData && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <iframe
                src={reportData.reportUrl}
                width="100%"
                style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  minHeight: 'calc(100vh - 400px)',
                  height: 'calc(100vh - 400px)',
                  maxHeight: '1200px'
                }}
                title="Consultation Report"
              />
            </Box>
          )}

          {/* Download Button */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            {reportData && (<Button
              variant="outlined"
              startIcon={<Download />}
              href={reportData?.reportUrl}
              target="_blank"
              download
            >
              Download Report
            </Button>)}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Response Section */}
          {reportData && (responseSubmitted ? (
            <Alert 
              severity={reportData.vendorResponse === "I'd like to proceed" ? 'success' : reportData.vendorResponse === 'Not interested' ? 'warning' : 'info'}
              sx={{ mb: 2 }}
            >
              <Typography variant="body1" fontWeight="bold">
                Your Response: {reportData.vendorResponse}
              </Typography>
              {reportData.vendorResponseDate && (
                <Typography variant="body2" color="text.secondary">
                  Submitted on {new Date(reportData.vendorResponseDate).toLocaleString()}
                </Typography>
              )}
              {reportData.vendorSignatureImageData && reportData.vendorSignatureImageType && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Your Signature:
                  </Typography>
                  <Box
                    component="img"
                    src={`data:${reportData.vendorSignatureImageType};base64,${reportData.vendorSignatureImageData}`}
                    alt="Vendor signature"
                    sx={{
                      maxWidth: 300,
                      maxHeight: 120,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      p: 1,
                      bgcolor: 'white'
                    }}
                  />
                </Box>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Thank you for your response. The agency has been notified.
              </Typography>
            </Alert>
          ) : (
            <>
              <Typography variant="h6" gutterBottom align="center">
                Please provide your response:
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<CheckCircle />}
                  onClick={() => handleResponse("I'd like to proceed")}
                  disabled={submitting}
                  sx={{ minWidth: 180 }}
                >
                  {submitting ? 'Submitting...' : "I'd like to proceed"}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<Cancel />}
                  onClick={() => handleResponse('Not interested')}
                  disabled={submitting}
                  sx={{ minWidth: 180 }}
                >
                  {submitting ? 'Submitting...' : 'Not interested'}
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  size="large"
                  startIcon={<QuestionAnswer />}
                  onClick={() => handleResponse('Need to discuss')}
                  disabled={submitting}
                  sx={{ minWidth: 180 }}
                >
                  {submitting ? 'Submitting...' : 'Need to discuss'}
                </Button>
              </Box>
            </>
          ))}
        </CardContent>
      </Card>
    </Container>
  );
};

export default VendorReport;

